import { FC, useContext, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useYupValidationResolver } from '@vs-online-only-app/ui';
import { CreateAccountContext } from '../../CreateAccountContext';
import PasswordField from './PasswordField';
import CreateAccountError from './CreateAccountError';

export const createPasswordValidationSchema = yup.object({
  password: yup
    .string()
    .required('Required')
    .matches(
      /^(?=.*[\p{L}\p{N}\p{P}\p{S}])(?=.*[0-9!@#$%^&*])[^\s]{8,}$/u,
      'Format requirement not met',
    ),
  confirmPassword: yup
    .string()
    .required('Required')
    .oneOf([yup.ref('password')], "Passwords don't match"),
});

export type PasswordFormFieldValues = {
  password: string;
  confirmPassword: string;
};

const EnterPassword: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [associateRegisterError, setAssociateRegisterError] = useState(false);

  const { createAccount } = useContext(CreateAccountContext);
  const resolver = useYupValidationResolver(createPasswordValidationSchema);
  const { handleSubmit, register, formState } =
    useForm<PasswordFormFieldValues>({
      defaultValues: {
        password: '',
        confirmPassword: '',
      },
      resolver,
    });

  const { errors } = formState;
  const error = errors.password || errors.confirmPassword;

  const handleFormSubmit = () => {
    setAssociateRegisterError(false);

    handleSubmit(async (formValues) => {
      const { password, confirmPassword } = formValues;
      setIsLoading(true);
      const success = await createAccount(password, confirmPassword);
      if (!success) setAssociateRegisterError(true);
      setIsLoading(false);
    })();
  };

  return (
    <>
      <CreateAccountError
        isOpen={associateRegisterError}
        handleClick={handleFormSubmit}
      />
      {error && (
        <Alert severity="error" sx={{ width: '-webkit-fill-available' }}>
          <AlertTitle>Invalid password</AlertTitle>
          {error.message}
        </Alert>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleFormSubmit();
        }}
        style={{ width: '100%' }}
      >
        <Stack gap={4}>
          <Stack gap={2}>
            <PasswordField
              fieldName="password"
              errors={errors}
              inputProps={{ ...register('password') }}
            />
            <PasswordField
              fieldName="confirmPassword"
              errors={errors}
              inputProps={{ ...register('confirmPassword') }}
            />
          </Stack>
          <Button
            type="submit"
            fullWidth
            size="large"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Create account'}
          </Button>
        </Stack>
      </form>
    </>
  );
};

export default EnterPassword;
